import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

interface UserPlan {
  plan_name: string;
  plan_display_name: string;
  status: string;
  monthly_tgs_limit: number;
  ai_generation: boolean;
  manual_mode: boolean;
  export_formats: string[];
  custom_spacing: boolean;
  api_access: boolean;
  current_usage: {
    tgs_generated: number;
    exports_pdf: number;
    exports_png: number;
    exports_jpeg: number;
  };
  usage_percentage: number;
  is_trial?: boolean;
  trial_end?: string;
}

interface AuthStatus {
  authenticated: boolean;
  user_id?: string;
  plan?: UserPlan;
}

export const useAuth = () => {
  const { user, isLoaded: userLoaded } = useUser();
  const { getToken, isLoaded: authLoaded } = useClerkAuth();
  const [authStatus, setAuthStatus] = useState<AuthStatus>({ authenticated: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trialEligible, setTrialEligible] = useState<boolean | null>(null);

  // Set up axios interceptor for authentication
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      async (config) => {
        if (user && authLoaded) {
          const token = await getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [user, authLoaded, getToken]);

  // Fetch auth status when user is loaded
  useEffect(() => {
    const fetchAuthStatus = async () => {
      if (!userLoaded || !authLoaded) return;

      try {
        setLoading(true);
        setError(null);

        if (!user) {
          setAuthStatus({ authenticated: false });
          return;
        }

        // Check auth status with backend
        const base = process.env.REACT_APP_API_URL || '';
        const response = await axios.get(`${base}/api/auth/status`);
        setAuthStatus(response.data);

      } catch (err: any) {
        console.error('Auth status check failed:', err);
        if (err.response?.status === 401) {
          setAuthStatus({ authenticated: false });
        } else {
          setError('Failed to check authentication status');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAuthStatus();
  }, [user, userLoaded, authLoaded]);

  // Check trial eligibility for unpaid users
  useEffect(() => {
    const checkTrialEligibility = async () => {
      if (!user) return;
      try {
        const base = process.env.REACT_APP_API_URL || '';
        const response = await axios.get(`${base}/api/subscription/trial/eligibility`);
        setTrialEligible(response.data.eligible);
      } catch (err) {
        console.error('Failed to check trial eligibility:', err);
        setTrialEligible(false);
      }
    };
    
    if (authStatus.plan?.plan_name === 'unpaid') {
      checkTrialEligibility();
    } else {
      setTrialEligible(false);
    }
  }, [user, authStatus]);

  const checkQuota = async (actionType: string): Promise<{ canProceed: boolean; message?: string; details?: any }> => {
    try {
      // This would be called before performing actions
      // For now, we'll check based on current usage
      if (!authStatus.plan) {
        return { canProceed: false, message: 'Plan information not available' };
      }

      const { plan } = authStatus;

      if (actionType === 'tgs_generated') {
        if (plan.current_usage.tgs_generated >= plan.monthly_tgs_limit) {
          return {
            canProceed: false,
            message: `Monthly TGS generation limit (${plan.monthly_tgs_limit}) exceeded`,
            details: { current: plan.current_usage.tgs_generated, limit: plan.monthly_tgs_limit }
          };
        }
      } else if (actionType.startsWith('export_')) {
        const format = actionType.split('_')[1].toUpperCase();
        if (!plan.export_formats.includes(format)) {
          return {
            canProceed: false,
            message: `Export format ${format} not available in ${plan.plan_display_name} plan`,
            details: { requestedFormat: format, allowedFormats: plan.export_formats }
          };
        }
      }

      return { canProceed: true };

    } catch (err) {
      console.error('Quota check failed:', err);
      return { canProceed: false, message: 'Failed to check quota' };
    }
  };

  const refreshAuthStatus = async () => {
    if (!user) return;

    try {
      const base = process.env.REACT_APP_API_URL || '';
      const response = await axios.get(`${base}/api/auth/status`);
      setAuthStatus(response.data);
    } catch (err) {
      console.error('Failed to refresh auth status:', err);
    }
  };

  const startTrial = async (billingInterval: 'month' | 'year' = 'month') => {
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(
        `${base}/api/subscription/trial/checkout`,
        {
          billing_interval: billingInterval,
          success_url: window.location.origin + '/?trial=success',
          cancel_url: window.location.origin + '/?trial=cancelled'
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Failed to start trial:', err);
      throw err;
    }
  };

  return {
    user,
    getToken, // Export getToken for API calls
    authStatus,
    loading: loading || !userLoaded || !authLoaded,
    error,
    checkQuota,
    refreshAuthStatus,
    startTrial,
    trialEligible,
    isAuthenticated: authStatus.authenticated,
    plan: authStatus.plan
  };
};
